/**
 * Renders a table of enquiries when the AI returns type: "enquiry_list".
 * Matches AllEnquiries page: MUI Table with sticky header, avatar + name cell, status Chip, email/phone links.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Avatar,
  Chip,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import VerifiedIcon from "@mui/icons-material/Verified";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import FlagIcon from "@mui/icons-material/Flag";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import styles from "./EnquiryList.module.scss";

function getStatusColor(status) {
  switch (status) {
    case "New Enquiry":
      return "warning";
    case "Contacted":
      return "info";
    case "Application Form Filled":
      return "secondary";
    case "Pending Invoice Payment":
      return "primary";
    case "Pending COE":
    case "Pending Visa Submission":
    case "Pending Visa Decision":
      return "info";
    case "Study Start":
    case "Visa Approved":
      return "success";
    case "Visa Rejected":
      return "error";
    default:
      return "default";
  }
}

function getStatusIcon(status) {
  switch (status) {
    case "New Enquiry":
      return <FileUploadIcon fontSize="small" />;
    case "Contacted":
      return <VerifiedIcon fontSize="small" />;
    case "Application Form Filled":
      return <ReceiptIcon fontSize="small" />;
    case "Pending Invoice Payment":
      return <PaymentIcon fontSize="small" />;
    case "Pending COE":
      return <FlagIcon fontSize="small" />;
    case "Pending Visa Submission":
      return <SendIcon fontSize="small" />;
    case "Pending Visa Decision":
    case "Study Start":
    case "Visa Approved":
      return <CheckCircleIcon fontSize="small" />;
    case "Visa Rejected":
      return <CancelIcon fontSize="small" />;
    default:
      return null;
  }
}

function formatDate(isoStr) {
  if (!isoStr) return "—";
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return isoStr;
  }
}

function calculateAge(dateString) {
  if (!dateString) return "N/A";
  try {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  } catch {
    return "N/A";
  }
}

export default function EnquiryList({ data = [] }) {
  const navigate = useNavigate();

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, border: "1px solid #e9ecef" }}>
        <Typography variant="body2" color="text.secondary">
          No enquiries to show.
        </Typography>
      </Paper>
    );
  }

  const handleViewEnquiry = (enquiry) => {
    navigate("/enquiry_profile", { state: { enquiry } });
  };

  return (
    <Paper variant="outlined" className={styles.tableContainer} sx={{ borderRadius: 2, border: "1px solid #e9ecef", overflow: "hidden" }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f8f9fe" }}>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>NIC</TableCell>
            <TableCell>Age</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Country</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow
              key={row.id}
              hover
              onClick={() => handleViewEnquiry(row)}
              className={styles.tableRow}
            >
              <TableCell>{row.id}</TableCell>
              <TableCell>
                <Box className={styles.nameCell}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main", fontSize: 14 }}>
                    {(row.first_name || "N").charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" className={styles.primaryText}>
                      {row.first_name || ""} {row.last_name || ""}
                    </Typography>
                    <Typography variant="caption" className={styles.secondaryText}>
                      {row.email || "—"}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  icon={getStatusIcon(row.status)}
                  label={row.status || "N/A"}
                  color={getStatusColor(row.status)}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{row.nic || "N/A"}</TableCell>
              <TableCell>{calculateAge(row.date_of_birth)}</TableCell>
              <TableCell>
                {row.email ? (
                  <a
                    href={`mailto:${row.email}`}
                    className={styles.emailLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.email}
                  </a>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell>
                {row.phone_number ? (
                  <a
                    href={`tel:${row.phone_number}`}
                    className={styles.phoneLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.phone_number}
                  </a>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell>
                <Box className={styles.countryCell}>
                  {row.country_of_study || "N/A"}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
